/* table */
import { CellContext } from '@tanstack/react-table';

/* Shared */
import { Route } from 'application/Shared/Helpers';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { templateInterpolation } from '../Dashboard/Helpers/templateInterpolation';
import { Variables } from '../Dashboard/types';

interface Action {
    href: string;
    label: string;
}

export interface LinkTextProps {
    external?: boolean;
    action?: Action;
}

export type LinkTextCellProps<TData> = CellContext<TData, string> &
    LinkTextProps;

/**
 * A React component that renders a button as a link or an external link based on the row data.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.row - The row data object containing the cell value and other properties.
 * @returns {JSX.Element} The rendered component.
 */
export const LinkTextCell = <TData,>({
    cell,
    row,
    variables = {}
}: LinkTextCellProps<TData> & {
    variables?: Variables;
}): JSX.Element | null => {
    const { t } = useTranslation();

    const { clientId, partnerId } = useParams();

    const buildBaseRoute = Route.buildBaseRoute({
        clientId,
        partnerId
    });

    // @ts-expect-error TData
    const cellHref = row.original.footer?.actions[0]?.href;
    const cellLabel = templateInterpolation(cell.getValue(), variables);

    const isPartialPath = cellHref?.startsWith('/');

    const isExternalLink = cellHref?.startsWith('http');

    if (!cellLabel) return null;

    if (!cellHref && cellLabel) return <>{t(cellLabel)}</>;

    // Render a React Router Link component with the row data's page property as the path
    return (
        <Link
            to={isPartialPath ? `${buildBaseRoute}${cellHref}` : cellHref}
            key={cellLabel}
            {...(isExternalLink
                ? { rel: 'noopener noreferrer nofollow', target: '_blank' }
                : {})}
            {...(isPartialPath ? {} : { relative: 'path' })}
            data-trackid="linkTextCell_routeTo"
            className="link underline"
        >
            {t(cellLabel)}
        </Link>
    );
};
